<template>
    <app-layout>
        <base-header :shadow="false" border border-bg-from="from-fuchsia-500" border-bg-to="to-violet-500">
            <div class="flex items-center justify-between w-full">
                <h4 class="text-xl font-medium">
                    {{ $t('general.evaluations') }}
                </h4>
            </div>
        </base-header>

        <base-card flush>
            <template #body>
                <div class="flex items-center justify-between w-full space-x-2">
                    <multiselect v-model="form.evaluationType"
                                 :allow-empty="false"
                                 :options="form.evaluationTypeOptions"
                                 :placeholder="$t('evaluation.choose_type')"
                                 label="label"
                                 track-by="value"
                    />

                    <multiselect v-if="form.evaluationType.value === 'admins'"
                                 v-model="form.selectedAdmins"
                                 :options="admins"
                                 :placeholder="'Admins'"
                                 label="name"
                                 track-by="id"
                    />

                    <multiselect v-if="form.evaluationType.value === 'companies'"
                                 v-model="form.selectedCompanies"
                                 :options="companies"
                                 :placeholder="$t('evaluation.choose_companies')"
                                 label="name"
                                 multiple
                                 track-by="id"
                    />

                    <multiselect v-if="localStores.length > 0 && (form.evaluationType.value === 'stores' || form.evaluationType.value === 'admins')"
                                 v-model="form.selectedStores"
                                 :options="localStores"
                                 :placeholder="$t('evaluation.choose_stores')"
                                 label="name"
                                 multiple
                                 track-by="id"
                    />

                    <multiselect v-model="form.dataType"
                                 :allow-empty="false"
                                 :options="form.dataTypeOptions"
                                 :placeholder="$t('evaluation.choose_data_type')"
                                 label="label"
                                 track-by="value"
                    />

                    <datepicker v-if="form.dataType"
                                v-model="form.date"
                                :clearable="false"
                                :format="form.dataType.value.startsWith('budget') ? 'YYYY' : 'MMMM YYYY'"
                                :type="form.dataType.value.startsWith('budget') ? 'year' : 'month'"
                    />

                    <default-button :disabled="!canRequest" :loading="isLoading" icon="refresh"
                                    @click.native="updateColumns"
                    >
                        <span class="whitespace-nowrap">{{ $t('evaluation.load_data') }}</span>
                    </default-button>

                    <multiselect v-if="canRequestBmdExport"
                                 v-model="budgetExportType"
                                 :allow-empty="false"
                                 :options="budgetExportTypeOptions"
                                 :placeholder="$t('evaluation.choose_export_type')"
                                 label="label"
                                 track-by="value"
                    />

                    <template v-if="!canRequestBmdExport">
                        <default-button :disabled="!canRequest" :loading="isLoading" icon="document-download"
                                        :href="exportUrl" color="orange" should-target-blank
                        >
                            <span class="whitespace-nowrap">{{ $t('evaluation.export_data') }}</span>
                        </default-button>
                    </template>

                    <template v-else>
                        <default-button :disabled="!canRequestBudgetExport" :loading="isLoading"
                                        icon="document-download"
                                        :href="exportUrl" color="orange"
                                        should-target-blank
                        >
                            <span class="whitespace-nowrap">{{ $t('evaluation.export_data') }}</span>
                        </default-button>
                    </template>
                </div>
            </template>
        </base-card>

        <template v-if="!isLoading && evaluationData && currentDataType && currentDataType.value === 'budgets'">
            <budget-evaluation
                :evaluation-data="evaluationData"
                :date="currentDate.toDate()"
                @projection-toggle="handleProjectionToggle"
            />
        </template>

        <template v-if="!isLoading && evaluationData && currentDataType && currentDataType.value === 'budget_projections'">
            <budget-projection-evaluation
                :evaluation-data="evaluationData"
                :date="currentDate.toDate()"
            />
        </template>

        <template
            v-else-if="!isLoading && evaluationData && currentDataType && currentDataType.value === 'profit_and_loss'"
        >
            <profit-and-loss-evaluation
                :evaluation-data="evaluationData"
                :date="currentDate.toDate()"
            />
        </template>
    </app-layout>
</template>


<script>
import BudgetProjectionEvaluation from '@/Pages/Evaluation/Components/BudgetProjectionEvaluation';
import BudgetEvaluation from '@/Pages/Evaluation/Components/BudgetEvaluation';
import ProfitAndLossEvaluation from '@/Pages/Evaluation/Components/ProfitAndLossEvaluation';
import {cloneWith} from "lodash";

export default {
    components: {ProfitAndLossEvaluation, BudgetEvaluation, BudgetProjectionEvaluation},

    metaInfo() {
        return {
            title: this.$t('general.evaluation'),
        };
    },

    props: {
        companies: {
            type: Array,
        },
        stores: {
            type: Array,
        },
        admins: {
            type: Array,
        }
    },

    data() {
        return {
            isLoading: false,
            requestToken: null,
            hasDefaultRows: [],
            localStores: this.stores,

            rows: [
                storeManager.getFieldByNumber(1),
                storeManager.getFieldByNumber(52),
                storeManager.getFieldByNumber(60),
                storeManager.getFieldByNumber(98),
                storeManager.getFieldByNumber(105),
                storeManager.getFieldByNumber(600),
            ],
            currentDate: moment.utc(new Date()),
            currentDataType: null,
            evaluationData: null,
            isShowProjections: false,

            form: {
                evaluationType: null,
                evaluationTypeOptions: [
                    {value: 'overall', label: this.$t('general.overall')},
                    {value: 'companies', label: this.$t('general.companies')},
                    {value: 'stores', label: this.$t('general.stores')},
                    {value: 'admins', label: 'Admins'}
                ],

                selectedCompanies: [],
                selectedStores: [],
                selectedAdmins: [],

                dataType: null,
                dataTypeOptions: [
                    {value: 'profit_and_loss', label: this.$t('general.profit_and_loss')},
                    {value: 'budgets', label: this.$t('general.budgets')},
                    {value: 'budget_projections', label: this.$t('general.budget_projections')},
                ],

                date: new Date(),
            },

            budgetExportModalIsOpen: false,
            budgetExportType: null,
            budgetExportTypeOptions: [
                {value: 'default', label: this.$t('general.default')},
                {value: 'bmd', label: this.$t('storemanager.bmd')},
            ]
        };
    },

    created() {
        this.form.evaluationType = this.form.evaluationTypeOptions[0];
        this.form.dataType = this.form.dataTypeOptions[0];
        this.budgetExportType = this.budgetExportTypeOptions[0];
    },

    computed: {
        selectedModels() {
            if (this.form.evaluationType.value === 'companies') {
                return this.form.selectedCompanies;
            } else if (this.form.evaluationType.value === 'stores') {
                return this.form.selectedStores;
            } else if (this.form.evaluationType.value === 'admins') {
                return this.form.selectedStores;
            }

            return [];
        },
        canRequest() {
            if (!this.form.evaluationType || !this.form.dataType || !this.form.date) {
                return false;
            }

            if (this.form.evaluationType.value !== 'overall') {
                if (!this.form.dataType) {
                    return false;
                }

                if (this.selectedModels.length === 0) {
                    return false;
                }
            }

            return true;
        },
        canRequestBmdExport() {
            return this.canRequest && this.form.evaluationType.value === 'stores' && this.selectedModels.length === 1 &&
                this.form.dataType.value === 'budgets';
        },
        canRequestBudgetExport() {
            return this.canRequest && !!this.budgetExportType && this.form.dataType.value.startsWith('budget');
        },
        exportUrl() {
            if (this.form.dataType.value.startsWith('budget')) {
                return route('evaluations.accumulated.export', {
                    type: this.form.evaluationType.value,
                    selected: this.selectedModels.map(model => model.id),
                    data_type: this.form.dataType.value,
                    export_type: this.budgetExportType.value,
                    date: moment.utc(this.form.date).year(),
                    isShowProjection: this.isShowProjections
                });
            }

            return route('evaluations.accumulated.export', {
                type: this.form.evaluationType.value,
                selected: this.selectedModels.map(model => model.id),
                data_type: this.form.dataType.value,
                date: this.form.date,
            });
        },
    },

    methods: {
        // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
        async handleProjectionToggle(isProjectionVisible) {
            this.isShowProjections = isProjectionVisible;
        },
        async updateColumns() {
            this.reset();
            this.isLoading = true;
            this.currentDate = moment.utc(this.form.date);
            this.currentDataType = this.form.dataType;
            this.evaluationData = await this.fetchData();
            this.isLoading = false;
        },
        fetchData() {
            this.requestToken = axios.CancelToken.source();

            /*if (this.form.evaluationType.value === 'admins') {
                this.form.evaluationType.value = 'stores';
            }*/

            return axios.get(route('api.evaluations.evaluate'), {
                params: {
                    type: this.form.evaluationType.value,
                    selected: this.selectedModels.map(model => model.id),
                    data_type: this.form.dataType.value,
                    date: this.form.dataType.value.startsWith('budget')
                        ? moment.utc(this.form.date).year()
                        : this.form.date,
                    isShowProjection: this.isShowProjections
                },
                cancelToken: this.requestToken.token,
            }).then(response => {
                return response.data;
            }).catch(error => {
                this.isLoading = false;

                if (!axios.isCancel(error)) {
                    throw error;
                }

                return null;
            });
        },
        reset() {
            this.requestToken?.cancel();
            this.isLoading = false;
            this.currentDate = null;
            this.evaluationData = null;
        },
    },

    watch: {
        // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
        stores(newVal) {
            this.localStores = newVal;
        },
        // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
        'form.selectedAdmins': function (newVal) {
            this.form.selectedStores = [];
            this.reset();
            if (newVal && Array.isArray(newVal.stores)) {
                this.localStores = [...newVal.stores];
                this.selectedStores = this.localStores;
                this.form.selectedStores.push(...newVal.stores);
            } else {
                this.localStores = [...this.stores];
                this.selectedStores = this.localStores;
            }
        },
        // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
        'form.evaluationType': function (newVal) {
            if (newVal.value === 'admins') {
                this.localStores = [...this.stores];
                this.form.selectedAdmins = [];
                this.form.selectedStores = [];
            } else if (newVal.value === 'stores') {
                this.localStores = this.stores;
                this.form.selectedAdmins = [];
                this.form.selectedStores = [];
            }
        }
    }
};
</script>
